import { X } from "lucide-react";

export interface ShortcutGroup {
  title: string;
  rows: Array<[string, string]>;
}

const GROUPS: ShortcutGroup[] = [
  {
    title: "Transport",
    rows: [
      ["Space · ⇧Space", "play / pause · play from the selection's start"],
      ["Home · Enter", "back to the start"],
      ["shift-drag the ruler", "loop region · \"punch\" beside it keeps only the part of a take inside it"],
      ["grid off", "drags snap to other clips' edges instead"],
      ["Mixer", "faders, pans, meters for every lane (faders and pans are the Set's)"],
      ["Devices", "the focused lane's, the master's or a return's device chain — a released slider proposes the parameter; \"+ device\" inserts one of Live's built-ins into every Set, ✕ removes one; \"hear (approx.)\" stands in a browser equivalent for the devices and MIDI effects it recognises, driven by the Set's values — not Live's sound"],
    ],
  },
  {
    title: "Arrangement",
    rows: [
      ["click · shift-click", "select · add to selection"],
      ["drag empty space", "rubber-band select (also a time selection)"],
    ["time selection: To scene", "consolidate the span to a new Session View scene (MIDI as notes, audio rendered dry into the pool)"],
      ["drag a clip · its edge", "move · resize (snaps to the grid)"],
      ["⌥-drag a clip", "copy it (or the selection) to the drop, same lane or another"],
      ["← →  (⇧ ×4)", "nudge the selection by the grid"],
      ["↑ ↓", "move the selection to the lane above / below (same kind, if free)"],
      ["lane header ▲ ▼", "reorder the lane — the Set's display order, synced (Live keeps its own)"],
      ["L · [ · ]", "drop a locator at the playhead · jump to the previous / next locator (locators are the Set's cue points)"],
      ["⌘A · Esc", "select all · clear selection"],
      ["⌘C · ⌘X · ⌘V · ⌘D", "copy · cut · paste at the playhead · duplicate"],
      ["Delete", "delete the selected clips"],
      ["M", "mute / unmute the selected clips in the Set"],
      ["⌘Z · ⇧⌘Z · ⌘Y", "undo · redo (\"history\" in the toolbar lists them — click one to undo back to it)"],
      ["⇧M · ⇧S · ⇧R", "mute · solo · arm every lane the selection touches (the Set's)"],
      ["double-click a MIDI lane", "draw an empty clip"],
      ["double-click a MIDI clip", "open the piano roll"],
      ["drop files on a lane", "audio → clips at the beat · .mid → one clip per track"],
      ["⌘ + scroll", "zoom about the pointer"],
      ["Z · ⇧Z", "zoom to the selection · fit everything"],
      ["clip view: drag the loop brace", "change what a warped clip repeats (synced; the inspector has the numbers)"],
      ["inspector: at BPM", "the tempo a warped clip's object is at — the browser stretches it onto the room's tempo from there (re-pitched in Re-Pitch mode), as Live does; dropped files with a pulse are warped at their detected tempo"],
      ["toolbar: meter", "the bars, bar lines, click and launch-on-bar count in it (this browser's view of the jam)"],
      ["?", "this sheet"],
    ],
  },
  {
    title: "Piano roll",
    rows: [
      ["double-click · right-click", "draw a note (as long as the last one drawn) · erase one — right-drag sweeps"],
      ["B · P", "draw mode: press to draw a note, drag right for its length · paint mode: sweep to lay a note in every cell crossed"],
      ["stamp", "a draw places a chord on the pressed note instead of one note"],
      ["drag · right edge", "move · resize (chords move whole)"],
      ["⌥-drag / ⇧-drag · ⌘-drag", "clone the notes · move off the grid"],
      ["↑ ↓  (⇧ octave) · ← →  (⇧ bar)", "transpose · nudge the selection"],
      ["⇧-click a key", "select every note on that pitch (⌘⇧ adds them)"],
      ["M · ⌘D / ⌘B · ⌘A", "mute · repeat after the span · select all"],
      ["⌘C · ⌘X · ⌘V", "copy · cut · paste at the playhead, else at the first bar in view (across clips too)"],
      ["ghosts", "the other MIDI lanes' notes under this clip, dimmed"],
      ["×2 · ÷2", "double the clip (notes repeating) · halve it"],
      ["steps", "a drum-machine grid over the clip: tap a cell for a hit, tap again to clear it, drag from an empty cell to paint a run, drag a hit to move it (along for time, across for another drum), right-click / right-drag erases, ⇧-tap cycles accent / ghost; a focused hit: Delete clears, arrows move it (⇧ a bar) — on by default for a kit lane"],
      ["scale", "tints the rows outside it and Fold snaps to it — the Set's scale while Live's Scale Mode is on, this browser's pick otherwise"],
      ["lane: vel · % · ±", "velocity · chance (how often a note plays) · velocity deviation — drag the bars; the preview rolls chance and deviation as Live does"],
      ["quantize · str % · swing %", "snap starts to a grid · only part of the way · with the off-beat late"],
      ["legato · ramp · humanize", "stretch to the next note · velocity ramp by time · nudge timing/velocity"],
      ["scale · fold", "tint rows outside a scale · snap notes onto it"],
      ["chop · arp · reverse", "grid repeats · chords as runs at the grid · mirror in time"],
      ["glue (⌘G) · flip · strum · flam", "join touching notes · mirror pitches · spread chords a 64th apart · a grace note before each"],
      ["softer · louder · ½ len · ×2 len", "velocities × 0.8 / × 1.25 · lengths halved / doubled"],
      ["Delete · Esc", "delete notes · clear selection / close"],
      ["keys: A W S E D F T G Y H U J K", "play notes (step input, or live with the transport)"],
      ["Z · X · ← → · Backspace", "octave down/up · move the step cursor · step back"],
    ],
  },
  {
    title: "Session View",
    rows: [
      ["click a clip", "launch it on the next bar (again to stop)"],
      ["▷ · ■ · capture · ● arr rec", "launch a scene · stop a track · copy what's launched onto the timeline · record launches into the arrangement as they play"],
      ["drag a clip", "to another slot, or onto the timeline · a library clip drops into an empty cell"],
      ["double-click an empty MIDI cell", "draw an empty clip"],
      ["✎ · ⧉ · + · ✕ · + scene", "rename · duplicate · insert above · delete a scene · add a row (all synced to the Set)"],
      ["● in an empty audio cell", "record the mic into that slot (■ to stop; the take lands there)"],
      ["keys, with a launched MIDI clip open", "overdub into it as it loops"],
    ],
  },
];

const FOOTER =
  "Everything on this sheet edits the room like any other change — it lands in everyone's Live Set. Playback controls (mute, solo, launch, timbre) are yours alone.";

/**
 * Every key the editor listens for, in one place.
 *
 * `groups` and `footer` are overridable because the browser room listens for a
 * subset of these, and a sheet that lists keys the page has not got is worse
 * than no sheet — someone presses L for a locator, nothing happens, and now
 * they doubt the ones that work.
 */
export function ShortcutsHelp({
  onClose,
  groups = GROUPS,
  footer = FOOTER,
}: {
  onClose: () => void;
  groups?: readonly ShortcutGroup[];
  footer?: string;
}) {
  return (
    <div
      role="dialog"
      aria-label="Keyboard shortcuts"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-10"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-lg border border-border bg-card p-4 text-xs shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium">Keyboard shortcuts</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground">
            <X className="size-4" />
          </button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {groups.map((g) => (
            <section key={g.title}>
              <h3 className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{g.title}</h3>
              <table className="w-full">
                <tbody>
                  {g.rows.map(([keys, what]) => (
                    <tr key={keys} className="align-top">
                      <td className="whitespace-nowrap pr-3 font-mono text-[11px] text-foreground">{keys}</td>
                      <td className="pb-0.5 text-muted-foreground">{what}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">{footer}</p>
      </div>
    </div>
  );
}
